<template>
  <div
    ref="root"
    class="relative flex w-full items-center rounded-xl bg-white p-3 lg:rounded-md"
    :class="[
      totalProviders === 1 ? 'justify-between' : 'flex-col gap-1 text-left',
    ]"
    style="border: 1px solid rgba(31, 36, 46, 0.14)"
    tabindex="0"
    @keydown.esc="menuOpen = false"
  >
    <div class="w-full text-left">
      <p
        v-if="!loading"
        class="text-left text-xs font-medium uppercase text-steel-1100"
      >
        {{ eldProvider.channel.name }}
      </p>
      <div
        v-else
        class="mb-1 h-3 w-1/2 animate-pulse rounded bg-steel-200"
      />
      <p
        v-if="!loading"
        class="text-xs font-normal text-steel-700"
      >
        Connected on: {{ dayjs(eldProvider.created_at).format('MM/DD/YY') }}
      </p>
      <div
        v-else
        class="h-3 w-2/5 animate-pulse rounded bg-steel-200"
      />
    </div>

    <div
      class="flex items-center gap-2"
      :class="[totalProviders === 1 ? '' : 'w-full']"
    >
      <p
        v-if="!loading"
        class="text-left text-xs text-steel-900"
        :class="[totalProviders === 1 ? 'mr-2 whitespace-nowrap' : 'mt-2']"
      >
        Last updated:
        {{ dayjs(eldProvider.updated_at).format('MM/DD/YY, h:mm a') }}
      </p>
      <div
        v-else
        class="h-3 w-1/5 animate-pulse rounded bg-steel-200"
      />
      <button
        v-if="showActions && actionsTrigger === 'menu'"
        ref="triggerEl"
        type="button"
        class="absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-md hover:bg-steel-100 focus:outline-none focus:ring-2 focus:ring-steel-400"
        aria-haspopup="menu"
        :aria-expanded="menuOpen"
        @click.stop="toggleMenu"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
        >
          <path
            d="M8.75 4.6875C8.75 4.44027 8.82331 4.1986 8.96066 3.99304C9.09802 3.78748 9.29324 3.62726 9.52165 3.53265C9.75005 3.43804 10.0014 3.41329 10.2439 3.46152C10.4863 3.50975 10.7091 3.6288 10.8839 3.80362C11.0587 3.97843 11.1778 4.20116 11.226 4.44364C11.2742 4.68611 11.2495 4.93745 11.1549 5.16586C11.0602 5.39426 10.9 5.58949 10.6945 5.72684C10.4889 5.86419 10.2472 5.9375 10 5.9375C9.66848 5.9375 9.35054 5.8058 9.11612 5.57138C8.8817 5.33696 8.75 5.01902 8.75 4.6875ZM10 8.75C9.75277 8.75 9.5111 8.82331 9.30554 8.96066C9.09998 9.09802 8.93976 9.29324 8.84515 9.52165C8.75054 9.75005 8.72579 10.0014 8.77402 10.2439C8.82225 10.4863 8.9413 10.7091 9.11612 10.8839C9.29093 11.0587 9.51366 11.1778 9.75614 11.226C9.99861 11.2742 10.2499 11.2495 10.4784 11.1549C10.7068 11.0602 10.902 10.9 11.0393 10.6945C11.1767 10.4889 11.25 10.2472 11.25 10C11.25 9.66848 11.1183 9.35054 10.8839 9.11612C10.6495 8.8817 10.3315 8.75 10 8.75ZM10 14.0625C9.75277 14.0625 9.5111 14.1358 9.30554 14.2732C9.09998 14.4105 8.93976 14.6057 8.84515 14.8341C8.75054 15.0626 8.72579 15.3139 8.77402 15.5564C8.82225 15.7988 8.9413 16.0216 9.11612 16.1964C9.29093 16.3712 9.51366 16.4903 9.75614 16.5385C9.99861 16.5867 10.2499 16.562 10.4784 16.4674C10.7068 16.3727 10.902 16.2125 11.0393 16.007C11.1767 15.8014 11.25 15.5597 11.25 15.3125C11.25 14.981 11.1183 14.663 10.8839 14.4286C10.6495 14.1942 10.3315 14.0625 10 14.0625Z"
            fill="#374053"
          />
        </svg>
      </button>

      <button
        v-else-if="showActions && actionsTrigger === 'refresh'"
        class="flex h-10 items-center gap-1 rounded-md bg-steel-20 px-3 text-sm font-medium text-neutral-subtle-bold hover:bg-hovered-gray active:bg-pressed-gray"
        @click.stop="emitRefresh"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M11.8284 2.99951L14.6569 5.82794L11.8284 8.65637V7.00227C10.5013 7.04021 9.22242 7.51746 8.19365 8.36175C7.12061 9.24237 6.38611 10.4678 6.1153 11.8293C5.84449 13.1907 6.05412 14.604 6.70849 15.8282C7.36285 17.0524 8.42145 18.0119 9.70391 18.5431C10.9864 19.0743 12.4134 19.1444 13.7417 18.7415C15.0701 18.3385 16.2176 17.4874 16.9888 16.3332C17.76 15.179 18.1072 13.7932 17.9711 12.4117C17.8351 11.0303 17.2242 9.73873 16.2427 8.75717L17.6569 7.34296C18.9656 8.6517 19.7801 10.3737 19.9615 12.2157C20.1429 14.0576 19.68 15.9055 18.6518 17.4444C17.6235 18.9833 16.0934 20.1181 14.3223 20.6553C12.5511 21.1926 10.6485 21.0991 8.93855 20.3909C7.22859 19.6826 5.81712 18.4033 4.94464 16.771C4.07216 15.1387 3.79265 13.2544 4.15373 11.4391C4.51481 9.62381 5.49414 7.98989 6.92486 6.81573C8.31133 5.67789 10.0386 5.04002 11.8284 5.00165V2.99951Z"
            fill="#6C7C9D"
          />
        </svg>
        <span class="text-steel-700">Refresh</span>
      </button>
    </div>
  </div>
  <Teleport to="body">
    <div
      v-show="menuOpen"
      ref="dropdownEl"
      class="z-[9999] w-36 rounded-xl bg-white py-2 shadow-lg ring-1 ring-black/5"
      :style="floatingStyle"
      role="menu"
    >
      <button
        class="block w-full px-4 py-2 text-left text-sm text-steel-900 hover:bg-steel-200"
        role="menuitem"
        @click="emitRefresh"
      >
        Refresh
      </button>
      <button
        class="block w-full px-4 py-2 text-left text-sm text-rose-700 hover:bg-rose-50"
        role="menuitem"
        @click="emitDelete"
      >
        Delete
      </button>
    </div>
  </Teleport>
</template>

<script setup>
import dayjs from 'dayjs';
import { ref, onMounted, onBeforeUnmount, defineProps, defineEmits } from 'vue';

const props = defineProps({
  eldProvider: { type: Object, required: true },
  totalProviders: { type: Number, required: true },
  showActions: { type: Boolean, default: false },
  actionsTrigger: {
    type: String,
    default: 'menu',
    validator: (v) => ['menu', 'refresh', 'none'].includes(v),
  },
  loading: { type: Boolean, default: false },
});

const emit = defineEmits(['refresh', 'delete']);

const root = ref(null);
const triggerEl = ref(null);
const dropdownEl = ref(null);
const menuOpen = ref(false);
const floatingStyle = ref({});

const toggleMenu = () => {
  menuOpen.value = !menuOpen.value;
  if (menuOpen.value) updateFloating();
};

const emitRefresh = () => {
  menuOpen.value = false;
  emit('refresh', props.eldProvider);
};
const emitDelete = () => {
  menuOpen.value = false;
  emit('delete', props.eldProvider);
};

const updateFloating = () => {
  if (!triggerEl.value) return;
  const r = triggerEl.value.getBoundingClientRect();
  const MENU_W = 144;
  floatingStyle.value = {
    position: 'fixed',
    top: `${r.bottom + 8}px`,
    left: `${r.right - MENU_W}px`,
  };
};
const onPointerDownGlobal = (e) => {
  if (!menuOpen.value) return;
  const t = e.target;
  const insideTrigger = !!triggerEl.value && triggerEl.value.contains(t);
  const insideDropdown = !!dropdownEl.value && dropdownEl.value.contains(t);
  const insideRoot = !!root.value && root.value.contains(t);
  if (!insideTrigger && !insideDropdown && !insideRoot) menuOpen.value = false;
};

onMounted(() => {
  document.addEventListener('pointerdown', onPointerDownGlobal, {
    capture: true,
  });
  window.addEventListener('resize', updateFloating);
  window.addEventListener('scroll', updateFloating, true);
});
onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', onPointerDownGlobal, {
    capture: true,
  });
  window.removeEventListener('resize', updateFloating);
  window.removeEventListener('scroll', updateFloating, true);
});
</script>
